import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BLOG_POSTS } from "../lib/data";
import CTASection from "../components/CTASection";
import { fadeUp } from "../lib/motion";

const paragraphs = [
  "There's a moment, somewhere between the third and fifth quiet week, when a business owner realises the enquiries just aren't coming in the way they used to. The traffic looks fine — but the phone doesn't ring, and the inbox stays neat.",
  "Nine times out of ten, it isn't the traffic. It's the website. Or more precisely, it's the fifty small design and technical decisions that quietly add up into 'this doesn't quite feel trustworthy'.",
  "The good news: most of these are cheap and quick to fix. The better news: the businesses that fix them are still relatively rare — which means fixing yours is a genuine, measurable advantage.",
  "Below is the shortlist we run every discovery call through. Not a full audit — just the five things that, in our experience, are almost always leaking leads.",
];

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <article className="pt-6 pb-24">
        <div className="container-page max-w-4xl">
          <Link to="/blog" className="badge-pill inline-flex mb-6" data-testid="blogpost-back">← Journal</Link>
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
              <span className="badge-pill">{post.category}</span>
              <span>{new Date(post.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
              <span>•</span>
              <span>{post.read}</span>
            </div>
            <h1 className="mt-6 text-4xl md:text-6xl font-extrabold tracking-tighter text-slate-900 dark:text-white leading-[1.05]">{post.title}</h1>
            <p className="mt-6 text-xl text-slate-600 dark:text-slate-300 leading-relaxed">{post.excerpt}</p>
          </motion.div>
          <motion.div {...fadeUp} className="mt-12 rounded-[24px] overflow-hidden border border-slate-200 dark:border-slate-800">
            <img src={post.cover} alt={post.title} className="w-full h-[380px] md:h-[520px] object-cover" />
          </motion.div>
          <div className="mt-12 prose prose-lg max-w-none text-slate-700 dark:text-slate-200 leading-relaxed" data-testid="blogpost-body">
            {paragraphs.map((p, i) => (
              <p key={i} className="mb-6 text-lg leading-[1.75]">{p}</p>
            ))}
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mt-12 mb-4">A short checklist</h2>
            <ul className="space-y-3 list-disc pl-6">
              <li>Above-the-fold clarity — visitors should know what you do in under three seconds.</li>
              <li>A single, obvious primary action on every page.</li>
              <li>Mobile-first everything — most of your traffic is on a phone.</li>
              <li>Speed. Under two seconds to interactive, or your bounce rate quietly doubles.</li>
              <li>Real photos, real names, real reviews. Trust is the whole game.</li>
            </ul>
            <p className="mt-8 italic text-slate-600 dark:text-slate-400">If any of the above sounded uncomfortably familiar — that's usually a good sign it's worth a proper look. Book a free discovery call and we'll walk through your site together.</p>
          </div>
        </div>
      </article>

      <section className="pb-16" data-testid="blogpost-related">
        <div className="container-page">
          <div className="flex items-end justify-between mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">Keep reading</h2>
            <Link to="/blog" className="btn-ghost">All articles <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="group" data-testid={`blogpost-related-${r.slug}`}>
                <div className="aspect-[4/3] rounded-[20px] overflow-hidden border border-slate-100 dark:border-slate-800">
                  <img src={r.cover} alt={r.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
                <h3 className="mt-4 font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 transition-colors">{r.title}</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{r.category}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
